import { useCallback, useEffect, useRef, useState } from "react";
import type { ApiResult } from "./api";

/**
 * Zero-dependency stale-while-revalidate cache for API responses.
 *
 * - Entries persist in localStorage (`alm.cache.v1:<key>`) with an in-memory
 *   mirror, so a page paints instantly from the last visit's data while a
 *   background refetch runs. State (and storage) update only when the payload
 *   actually changed.
 * - Responses vary by viewer (e.g. admin-only recipe steps), so the whole
 *   cache is purged whenever the signed-in identity changes (`setCacheOwner`).
 * - No TTL: data is always revalidated in the background on mount, never aged
 *   out by the clock — keeps tests wall-clock-free and semantics simple.
 */

const PREFIX = "alm.cache.v1:";
const OWNER_KEY = "alm.cache.owner";

const memory = new Map<string, string>();

export function readCache(key: string): string | null {
  const hit = memory.get(key);
  if (hit !== undefined) return hit;
  try {
    const stored = localStorage.getItem(PREFIX + key);
    if (stored !== null) memory.set(key, stored);
    return stored;
  } catch {
    return null;
  }
}

export function writeCache(key: string, serialized: string): void {
  memory.set(key, serialized);
  try {
    localStorage.setItem(PREFIX + key, serialized);
  } catch {
    // Quota exceeded / storage disabled: drop all persisted entries so the
    // freshest data wins the space, then retry once. Memory-only is fine too.
    try {
      purgePersisted();
      localStorage.setItem(PREFIX + key, serialized);
    } catch {
      /* memory-only */
    }
  }
}

export function dropCache(key: string): void {
  memory.delete(key);
  try {
    localStorage.removeItem(PREFIX + key);
  } catch {
    /* ignore */
  }
}

function purgePersisted(): void {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(PREFIX)) doomed.push(k);
    }
    for (const k of doomed) localStorage.removeItem(k);
  } catch {
    /* ignore */
  }
}

export function purgeApiCache(): void {
  memory.clear();
  purgePersisted();
}

/**
 * Purge everything when the signed-in identity changes (login/logout/switch).
 * Cached payloads are role-shaped (recipes, canEdit…), so they must never
 * survive across identities on a shared browser.
 */
export function setCacheOwner(owner: string): void {
  try {
    if (localStorage.getItem(OWNER_KEY) === owner) return;
    purgeApiCache();
    localStorage.setItem(OWNER_KEY, owner);
  } catch {
    purgeApiCache();
  }
}

export type CachePhase = "loading" | "refreshing" | "ready" | "error";

export interface CachedResource<T> {
  data: T | null;
  phase: CachePhase;
  error: string | null;
  httpStatus: number | null;
  refresh: () => Promise<void>;
  setData: (updater: (prev: T) => T) => void;
}

interface State<T> {
  key: string;
  data: T | null;
  phase: CachePhase;
  error: string | null;
  httpStatus: number | null;
}

function initState<T>(key: string, enabled: boolean): State<T> {
  const cached = enabled ? readCache(key) : null;
  if (cached !== null) {
    try {
      return { key, data: JSON.parse(cached) as T, phase: "refreshing", error: null, httpStatus: null };
    } catch {
      dropCache(key);
    }
  }
  return { key, data: null, phase: "loading", error: null, httpStatus: null };
}

export function useCachedResource<T>(
  key: string,
  fetcher: () => Promise<ApiResult<T>>,
  opts?: { enabled?: boolean }
): CachedResource<T> {
  const enabled = opts?.enabled ?? true;
  // The latest fetcher is always used, but its identity never triggers a
  // refetch — only `key` (and `enabled`) do. Callers can pass inline closures.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const [state, setState] = useState<State<T>>(() => initState<T>(key, enabled));

  // Key changed (slug/month navigation): re-seed from cache during render so
  // the new key's cached data paints in the same frame.
  if (state.key !== key) setState(initState<T>(key, enabled));

  const refresh = useCallback(async () => {
    if (!enabled) return;
    const res = await fetcherRef.current();
    setState((prev) => {
      if (prev.key !== key) return prev; // answer for a stale key — ignore
      if (res.success && res.data !== undefined) {
        const serialized = JSON.stringify(res.data);
        const prevSerialized = prev.data !== null ? JSON.stringify(prev.data) : null;
        if (serialized === prevSerialized) {
          return { ...prev, phase: "ready", error: null, httpStatus: res.status };
        }
        writeCache(key, serialized);
        return { key, data: res.data as T, phase: "ready", error: null, httpStatus: res.status };
      }
      if (res.status === 404) {
        // Deleted upstream — a stale copy must not linger.
        dropCache(key);
        return { key, data: null, phase: "error", error: res.error ?? "Not found", httpStatus: 404 };
      }
      return prev.data !== null
        ? { ...prev, phase: "ready", error: res.error ?? null, httpStatus: res.status }
        : { key, data: null, phase: "error", error: res.error ?? "Request failed", httpStatus: res.status };
    });
  }, [key, enabled]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const setData = useCallback((updater: (prev: T) => T) => {
    setState((prev) => {
      if (prev.data === null) return prev;
      const next = updater(prev.data);
      writeCache(prev.key, JSON.stringify(next));
      return { ...prev, data: next };
    });
  }, []);

  return { data: state.data, phase: state.phase, error: state.error, httpStatus: state.httpStatus, refresh, setData };
}
